// Pure input-validation functions shared across resolvers, Lambdas, and the
// frontend (single source of truth). See design.md → Error Handling and
// Correctness Properties 7 (input validation) and 20 (timing preferences).
//
// Result convention
// -----------------
// Every validator returns a `ValidationResult` discriminated union rather than
// throwing:
//
//   { ok: true }                                     // input is valid
//   { ok: false; field: string; message: string }   // input is invalid
//
// The non-`throw` approach keeps the functions pure and side-effect free, lets
// callers branch on `result.ok`, and carries the offending `field` plus a
// human-readable `message` that names the field and its permitted length (as
// required by Req 2.2 and 4.6). Callers that prefer exceptions can wrap a
// validator and throw when `ok` is false.
/** Convenience constructor for a successful validation result. */
const valid = () => ({ ok: true });
/** Convenience constructor for a failed validation result. */
const invalid = (field, message) => ({
    ok: false,
    field,
    message,
});
/**
 * True when `value` is a non-empty, non-whitespace-only string. A value that is
 * `undefined`, `null`, empty, or only whitespace is considered "blank".
 */
const isBlank = (value) => value === null || value === undefined || value.trim().length === 0;
// -- Inclusive length bounds (characters) ------------------------------------
/** Project name permitted length, inclusive. Req 2.1, 2.2. */
export const PROJECT_NAME_MIN = 1;
export const PROJECT_NAME_MAX = 100;
/** Course title permitted length, inclusive. Req 4.5, 4.6. */
export const COURSE_TITLE_MIN = 1;
export const COURSE_TITLE_MAX = 200;
/** Topic prompt permitted length, inclusive. Req 4.4, 4.6. */
export const TOPIC_PROMPT_MIN = 1;
export const TOPIC_PROMPT_MAX = 2000;
// -- Inclusive timing bounds (seconds) ---------------------------------------
/** Per-segment duration bounds, inclusive. Req 10.4. */
export const SEGMENT_DURATION_MIN_SECONDS = 1;
export const SEGMENT_DURATION_MAX_SECONDS = 600;
/** Inter-segment pause bounds, inclusive. Req 10.4. */
export const SEGMENT_PAUSE_MIN_SECONDS = 0;
export const SEGMENT_PAUSE_MAX_SECONDS = 60;
/**
 * Validate a project name.
 *
 * Accepts a string whose length is in [1, 100]; rejects empty, whitespace-only,
 * or over-length names. Length is measured on the raw string (a name of only
 * whitespace is rejected by the blank check, not the length check).
 *
 * Requirements: 2.1, 2.2. Property 7.
 */
export function validateProjectName(name) {
    if (isBlank(name)) {
        return invalid('name', `Project name must be ${PROJECT_NAME_MIN} to ${PROJECT_NAME_MAX} characters and cannot be empty or whitespace-only.`);
    }
    if (name.length < PROJECT_NAME_MIN || name.length > PROJECT_NAME_MAX) {
        return invalid('name', `Project name must be ${PROJECT_NAME_MIN} to ${PROJECT_NAME_MAX} characters.`);
    }
    return valid();
}
/**
 * Validate a course title.
 *
 * Accepts a string whose length is in [1, 200]. A whitespace-only title is
 * rejected (its permitted length rule is the same as the blank rule at the
 * lower bound), keeping title validation consistent with the other text fields.
 *
 * Requirements: 4.5, 4.6. Property 7.
 */
export function validateCourseTitle(title) {
    if (isBlank(title)) {
        return invalid('title', `Course title must be ${COURSE_TITLE_MIN} to ${COURSE_TITLE_MAX} characters and cannot be empty or whitespace-only.`);
    }
    if (title.length < COURSE_TITLE_MIN || title.length > COURSE_TITLE_MAX) {
        return invalid('title', `Course title must be ${COURSE_TITLE_MIN} to ${COURSE_TITLE_MAX} characters.`);
    }
    return valid();
}
/**
 * Validate a course topic prompt.
 *
 * Accepts a string whose length is in [1, 2000]; rejects empty, whitespace-only,
 * or over-length prompts.
 *
 * Requirements: 4.4, 4.6. Property 7.
 */
export function validateTopicPrompt(prompt) {
    if (isBlank(prompt)) {
        return invalid('topicPrompt', `Topic prompt must be ${TOPIC_PROMPT_MIN} to ${TOPIC_PROMPT_MAX} characters and cannot be empty or whitespace-only.`);
    }
    if (prompt.length < TOPIC_PROMPT_MIN || prompt.length > TOPIC_PROMPT_MAX) {
        return invalid('topicPrompt', `Topic prompt must be ${TOPIC_PROMPT_MIN} to ${TOPIC_PROMPT_MAX} characters.`);
    }
    return valid();
}
/**
 * True when `value` is a finite integer. Non-finite (`NaN`, `Infinity`) and
 * fractional values are rejected so durations/pauses are whole seconds.
 */
const isInteger = (value) => Number.isInteger(value);
/**
 * Validate project timing preferences.
 *
 * Accepts iff the per-segment duration is an integer in [1, 600] seconds and
 * the inter-segment pause is an integer in [0, 60] seconds. The per-segment
 * duration is checked first so its field/message is reported when both are out
 * of range.
 *
 * Requirements: 10.4. Property 20.
 */
export function validateTimingPreferences(prefs) {
    const { segmentDurationSeconds, interSegmentPauseSeconds } = prefs;
    if (!isInteger(segmentDurationSeconds) ||
        segmentDurationSeconds < SEGMENT_DURATION_MIN_SECONDS ||
        segmentDurationSeconds > SEGMENT_DURATION_MAX_SECONDS) {
        return invalid('segmentDurationSeconds', `Per-segment duration must be an integer number of seconds from ${SEGMENT_DURATION_MIN_SECONDS} to ${SEGMENT_DURATION_MAX_SECONDS}.`);
    }
    if (!isInteger(interSegmentPauseSeconds) ||
        interSegmentPauseSeconds < SEGMENT_PAUSE_MIN_SECONDS ||
        interSegmentPauseSeconds > SEGMENT_PAUSE_MAX_SECONDS) {
        return invalid('interSegmentPauseSeconds', `Inter-segment pause must be an integer number of seconds from ${SEGMENT_PAUSE_MIN_SECONDS} to ${SEGMENT_PAUSE_MAX_SECONDS}.`);
    }
    return valid();
}
//# sourceMappingURL=validation.js.map